/**
 * Pedir permiso para eliminar un adjunto del cajón (spec
 * 2026-09-24-solicitud-eliminacion). Lo ve quien no es supervisora: en vez de
 * borrar, escribe el motivo y la solicitud les llega a las supervisoras.
 */

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Input, Alert, Button, Tag, Typography } from 'antd';

import { deletionRequests } from '../../actions/database/deletion_requests';
import { validateReason, MIN_REASON_LENGTH, friendlyError, kindWithArticle } from '../../lib/deletion_request';

/**
 * @param {object}   request  {kind, itemName, itemPath, itemUrl, parentPath,
 *                            parentName, customerId, customerName}
 * @param {function} onUseToken  si viene, muestra "Tengo el token" (lleva al
 *                               borrado directo de siempre)
 */
export function RequestDeletionModal({ open, request, onCancel, onSent, onUseToken }) {
  const dispatch = useDispatch();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setReason(''); setError(null); setBusy(false); }
  }, [open]);

  const send = async () => {
    const invalid = validateReason(reason);
    if (invalid) { setError(invalid); return; }
    setBusy(true);
    setError(null);
    try {
      const { kind, itemName, itemPath, itemUrl, parentPath, parentName, customerId, customerName } = request;
      await dispatch(deletionRequests.create({
        kind, itemName, itemPath, itemUrl, parentPath, parentName, customerId, customerName, reason,
      }));
      onSent?.();
    } catch (e) {
      setError(friendlyError(e, 'No se pudo enviar la solicitud.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Solicitar eliminación"
      okText="Enviar solicitud"
      cancelText="Cancelar"
      okButtonProps={{ loading: busy, disabled: reason.trim().length < MIN_REASON_LENGTH }}
      cancelButtonProps={{ disabled: busy }}
      onOk={send}
      onCancel={busy ? undefined : onCancel}
      maskClosable={false}
      destroyOnHidden
    >
      <p>
        Vas a pedir que se elimine {kindWithArticle(request?.kind)}{' '}
        <strong>{request?.itemName}</strong>.
      </p>
      <Typography.Text type="secondary" style={{ fontSize: '0.9em' }}>
        La solicitud les llega a las supervisoras. Cuando una la apruebe, se elimina
        y queda en la papelera.
      </Typography.Text>
      <Input.TextArea
        style={{ marginTop: 16 }}
        rows={3}
        autoFocus
        placeholder={`Motivo (mínimo ${MIN_REASON_LENGTH} caracteres)`}
        value={reason}
        disabled={busy}
        onChange={(e) => setReason(e.target.value)}
      />
      {/* Sin esto el botón se apagaba sin decir por qué. */}
      {reason.trim().length < MIN_REASON_LENGTH ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Faltan {MIN_REASON_LENGTH - reason.trim().length} caracteres para poder enviar.
        </Typography.Text>
      ) : null}
      {error ? <Alert style={{ marginTop: 12 }} type="error" showIcon message={error} /> : null}
      {onUseToken ? (
        <Button type="link" style={{ paddingLeft: 0, marginTop: 8 }} disabled={busy} onClick={onUseToken}>
          Tengo el token
        </Button>
      ) : null}
    </Modal>
  );
}

/** Botón por fila: "Solicitar eliminación", o el tag si ya hay una pendiente. */
export function RequestDeletionControl({ pending, canCancel, onRequest, onCancelPending }) {
  if (pending) {
    return (
      <>
        <Tag color="orange">Eliminación solicitada</Tag>
        {canCancel ? <Button type="link" size="small" onClick={onCancelPending}>Cancelar</Button> : null}
      </>
    );
  }
  return <Button type="link" onClick={onRequest}>Solicitar eliminación</Button>;
}
